/**
 * Centralized API paths configuration
 * This ensures all API endpoints are defined in a single source of truth
 * Auth paths are defined in @/_auth/paths and merged here
 */

import { env } from '@/lib/env'
import { apiPathsAuth, apiPathKeysAuth } from '@/components/auth/paths'

const API_VERSION = 'v2'

/**
 * Get the API base URL from environment variables
 * @throws Error if VITE_API_BASE_URL is not set
 */
function getApiBaseUrl(): string {
  return env.apiBaseUrl
}

/**
 * Build a full API URL from a path
 */
function buildApiUrl(path: string): string {
  const baseUrl = getApiBaseUrl()
  const cleanBaseUrl = baseUrl.replace(/\/$/, '')
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${cleanBaseUrl}${cleanPath}`
}

/**
 * Single source of truth for API endpoint relative paths - V2 only
 * Auth paths come from _auth/paths
 */
const apiPathDefinitions = {
  /** A2A agent proxy – backend forwards to the A2A agent. Use as base URL for @a2a-js/sdk. */
  a2a: {
    agentProxyBase: `/api/${API_VERSION}/chat/agent`,
  },
  workflows: {
    /** Workflow catalog listing (GET /workflows). */
    catalog: {
      base: `/api/${API_VERSION}/workflows`,
    },
    workflowTypes: {
      base: `/api/${API_VERSION}/workflows/workflow-types`,
      byType: (workflowType: string) =>
        `/api/${API_VERSION}/workflows/workflow-types/${workflowType}`,
    },
    /** Catalog workflow by URL slug (hyphenated), e.g. `yield-excursion`. */
    byWorkflowSlug: {
      metadata: (workflowSlug: string) =>
        `/api/${API_VERSION}/workflows/${encodeURIComponent(workflowSlug)}`,
      versions: (workflowSlug: string) =>
        `/api/${API_VERSION}/workflows/${encodeURIComponent(workflowSlug)}/versions`,
      stats: (workflowSlug: string) =>
        `/api/${API_VERSION}/workflows/${encodeURIComponent(workflowSlug)}/stats`,
    },
    workflowRuns: {
      base: `/api/${API_VERSION}/workflows/workflow-runs`,
      byId: (runId: string) => `/api/${API_VERSION}/workflows/workflow-runs/${runId}`,
      // Accept number|string so callers can pass route params or numeric API values directly.
      byRunNumber: (runNumber: number | string) =>
        `/api/${API_VERSION}/workflows/workflow-runs/${runNumber}`,
      updates: `/api/${API_VERSION}/workflows/workflow-runs/updates`,
      logs: (runId: string) => `/api/${API_VERSION}/workflows/workflow-runs/${runId}/logs`,
      logsByRunNumber: (runNumber: number | string) =>
        `/api/${API_VERSION}/workflows/workflow-runs/${runNumber}/logs`,
      logUpdates: (runId: string) =>
        `/api/${API_VERSION}/workflows/workflow-runs/${runId}/log-updates`,
      cancel: (runId: string) => `/api/${API_VERSION}/workflows/workflow-runs/${runId}/cancel`,
      retry: (runId: string) => `/api/${API_VERSION}/workflows/workflow-runs/${runId}/retry`,
      stats: `/api/${API_VERSION}/workflows/workflow-runs/stats`,
      outputFilesDownload: (runId: string) =>
        `/api/${API_VERSION}/workflows/workflow-runs/${runId}/output-files/download`,
      report: (runId: string) => `/api/${API_VERSION}/workflows/workflow-runs/${runId}/report`,
      // Accept number|string so callers can pass route params or numeric API values directly.
      reportByRunNumber: (runNumber: number | string) =>
        `/api/${API_VERSION}/workflows/workflow-runs/${runNumber}/report`,
    },
  },
  products: {
    base: `/api/${API_VERSION}/products`,
    groups: `/api/${API_VERSION}/product-groups`,
    lots: (productId: string) => `/api/${API_VERSION}/products/${productId}/lots`,
    lotsMetrics: (productId: string) => `/api/${API_VERSION}/products/${productId}/lots/metrics`,
  },
  reports: {
    download: (reportId: string, type?: 'PDF' | 'DOCX' | 'PPT') =>
      `/api/${API_VERSION}/reports/${reportId}/download${type ? `?type=${type}` : ''}`,
    attachmentDownload: (reportId: string, attachmentId: string) =>
      `/api/${API_VERSION}/reports/${reportId}/attachments/${attachmentId}/download`,
    comments: (reportId: string, params?: { limit?: number; offset?: number }) => {
      const q = new URLSearchParams()
      if (params?.limit != null) q.set('limit', String(params.limit))
      if (params?.offset != null) q.set('offset', String(params.offset))
      const qs = q.toString()
      return `/api/${API_VERSION}/reports/${reportId}/comments${qs ? `?${qs}` : ''}`
    },
    exports: (params?: {
      report_id?: string | null
      active_only?: boolean
      limit?: number
      offset?: number
    }) => {
      const q = new URLSearchParams()
      if (params?.report_id) q.set('report_id', params.report_id)
      if (params?.active_only === true) q.set('active_only', 'true')
      if (params?.limit != null) q.set('limit', String(params.limit))
      if (params?.offset != null) q.set('offset', String(params.offset))
      const qs = q.toString()
      return `/api/${API_VERSION}/reports/exports${qs ? `?${qs}` : ''}`
    },
    exportsStream: `/api/${API_VERSION}/reports/exports/stream`,
  },
  dataSources: {
    base: `/api/${API_VERSION}/data-sources`,
  },
  dataSourceConnections: {
    base: `/api/${API_VERSION}/data-source-connections`,
    byId: (connectionId: string) =>
      `/api/${API_VERSION}/data-source-connections/${connectionId}`,
    test: (connectionId: string) =>
      `/api/${API_VERSION}/data-source-connections/${connectionId}/test`,
  },
  improvements: {
    base: `/api/${API_VERSION}/improvements`,
  },
  productConfigurations: {
    base: `/api/${API_VERSION}/product-configurations`,
  },
  productConfiguration: {
    byId: (id: string) => `/api/${API_VERSION}/product-configurations/${id}`,
  },
  userProducts: {
    base: `/api/${API_VERSION}/user/products`,
  },
  policies: {
    base: `/api/${API_VERSION}/policies`,
  },
  excursions: {
    base: `/api/${API_VERSION}/excursions`,
    metrics: `/api/${API_VERSION}/excursions/metrics`,
    byId: (eventId: string) => `/api/${API_VERSION}/excursions/${eventId}`,
    ticket: (eventId: string) => `/api/${API_VERSION}/excursions/${eventId}/ticket`,
  },
  feedback: {
    apply: (commentId: string) => `/api/${API_VERSION}/reports/comments/${commentId}/apply`,
    delete: (commentId: string) => `/api/${API_VERSION}/reports/comments/${commentId}`,
  },
} as const

/**
 * API endpoint paths - returns full URLs with base URL
 * V2 endpoints only; auth from _auth/paths
 */
const apiPaths = {
  auth: apiPathsAuth,
  a2a: {
    /** Base URL for @a2a-js/sdk createFromUrl(); must end with / so SDK resolves .well-known/agent-card.json under /agent. */
    agentProxyBase: () => {
      const url = buildApiUrl(apiPathDefinitions.a2a.agentProxyBase)
      return url.endsWith('/') ? url : `${url}/`
    },
  },
  workflows: {
    catalog: {
      base: () => buildApiUrl(apiPathDefinitions.workflows.catalog.base),
    },
    workflowTypes: {
      base: () => buildApiUrl(apiPathDefinitions.workflows.workflowTypes.base),
      byType: (workflowType: string) =>
        buildApiUrl(apiPathDefinitions.workflows.workflowTypes.byType(workflowType)),
    },
    byWorkflowSlug: {
      metadata: (workflowSlug: string) =>
        buildApiUrl(apiPathDefinitions.workflows.byWorkflowSlug.metadata(workflowSlug)),
      versions: (workflowSlug: string) =>
        buildApiUrl(apiPathDefinitions.workflows.byWorkflowSlug.versions(workflowSlug)),
      stats: (workflowSlug: string) =>
        buildApiUrl(apiPathDefinitions.workflows.byWorkflowSlug.stats(workflowSlug)),
    },
    workflowRuns: {
      base: () => buildApiUrl(apiPathDefinitions.workflows.workflowRuns.base),
      byId: (runId: string) => buildApiUrl(apiPathDefinitions.workflows.workflowRuns.byId(runId)),
      byRunNumber: (runNumber: number | string) =>
        buildApiUrl(apiPathDefinitions.workflows.workflowRuns.byRunNumber(runNumber)),
      updates: () => buildApiUrl(apiPathDefinitions.workflows.workflowRuns.updates),
      logs: (runId: string) => buildApiUrl(apiPathDefinitions.workflows.workflowRuns.logs(runId)),
      logsByRunNumber: (runNumber: number | string) =>
        buildApiUrl(apiPathDefinitions.workflows.workflowRuns.logsByRunNumber(runNumber)),
      logUpdates: (runId: string) =>
        buildApiUrl(apiPathDefinitions.workflows.workflowRuns.logUpdates(runId)),
      cancel: (runId: string) =>
        buildApiUrl(apiPathDefinitions.workflows.workflowRuns.cancel(runId)),
      retry: (runId: string) => buildApiUrl(apiPathDefinitions.workflows.workflowRuns.retry(runId)),
      stats: () => buildApiUrl(apiPathDefinitions.workflows.workflowRuns.stats),
      outputFilesDownload: (runId: string) =>
        buildApiUrl(apiPathDefinitions.workflows.workflowRuns.outputFilesDownload(runId)),
      report: (runId: string) =>
        buildApiUrl(apiPathDefinitions.workflows.workflowRuns.report(runId)),
      reportByRunNumber: (runNumber: number | string) =>
        buildApiUrl(apiPathDefinitions.workflows.workflowRuns.reportByRunNumber(runNumber)),
    },
  },
  products: {
    base: () => buildApiUrl(apiPathDefinitions.products.base),
    groups: () => buildApiUrl(apiPathDefinitions.products.groups),
    lots: (productId: string) => buildApiUrl(apiPathDefinitions.products.lots(productId)),
    lotsMetrics: (productId: string) =>
      buildApiUrl(apiPathDefinitions.products.lotsMetrics(productId)),
  },
  reports: {
    download: (reportId: string, type?: 'PDF' | 'DOCX' | 'PPT') =>
      buildApiUrl(apiPathDefinitions.reports.download(reportId, type)),
    attachmentDownload: (reportId: string, attachmentId: string) =>
      buildApiUrl(apiPathDefinitions.reports.attachmentDownload(reportId, attachmentId)),
    comments: (reportId: string, params?: { limit?: number; offset?: number }) =>
      buildApiUrl(apiPathDefinitions.reports.comments(reportId, params)),
    exports: (params?: {
      report_id?: string | null
      active_only?: boolean
      limit?: number
      offset?: number
    }) => buildApiUrl(apiPathDefinitions.reports.exports(params)),
    exportsStream: () => buildApiUrl(apiPathDefinitions.reports.exportsStream),
  },
  dataSources: {
    base: () => buildApiUrl(apiPathDefinitions.dataSources.base),
  },
  dataSourceConnections: {
    base: () => buildApiUrl(apiPathDefinitions.dataSourceConnections.base),
    byId: (connectionId: string) =>
      buildApiUrl(apiPathDefinitions.dataSourceConnections.byId(connectionId)),
    test: (connectionId: string) =>
      buildApiUrl(apiPathDefinitions.dataSourceConnections.test(connectionId)),
  },
  improvements: {
    base: () => buildApiUrl(apiPathDefinitions.improvements.base),
  },
  productConfigurations: {
    base: () => buildApiUrl(apiPathDefinitions.productConfigurations.base),
  },
  productConfiguration: {
    byId: (id: string) => buildApiUrl(apiPathDefinitions.productConfiguration.byId(id)),
  },
  userProducts: {
    base: () => buildApiUrl(apiPathDefinitions.userProducts.base),
  },
  policies: {
    base: () => buildApiUrl(apiPathDefinitions.policies.base),
    byProduct: (productId: string) => {
      const base = buildApiUrl(apiPathDefinitions.policies.base)
      return `${base}?product_id=${encodeURIComponent(productId)}`
    },
  },
  excursions: {
    base: () => buildApiUrl(apiPathDefinitions.excursions.base),
    metrics: () => buildApiUrl(apiPathDefinitions.excursions.metrics),
    byId: (eventId: string) => buildApiUrl(apiPathDefinitions.excursions.byId(eventId)),
    ticket: (eventId: string) => buildApiUrl(apiPathDefinitions.excursions.ticket(eventId)),
  },
  feedback: {
    apply: (commentId: string) => buildApiUrl(apiPathDefinitions.feedback.apply(commentId)),
    delete: (commentId: string) => buildApiUrl(apiPathDefinitions.feedback.delete(commentId)),
  },
} as const

/**
 * Get relative API paths (without base URL) for use in query keys
 * Auth keys from _auth/paths
 */
const apiPathKeys = {
  auth: apiPathKeysAuth,
  a2a: {
    agentProxyBase: () => apiPathDefinitions.a2a.agentProxyBase,
  },
  workflows: {
    catalog: {
      base: () => apiPathDefinitions.workflows.catalog.base,
    },
    workflowTypes: {
      base: () => apiPathDefinitions.workflows.workflowTypes.base,
      byType: (workflowType: string) =>
        apiPathDefinitions.workflows.workflowTypes.byType(workflowType),
    },
    byWorkflowSlug: {
      metadata: (workflowSlug: string) =>
        apiPathDefinitions.workflows.byWorkflowSlug.metadata(workflowSlug),
      versions: (workflowSlug: string) =>
        apiPathDefinitions.workflows.byWorkflowSlug.versions(workflowSlug),
      stats: (workflowSlug: string) =>
        apiPathDefinitions.workflows.byWorkflowSlug.stats(workflowSlug),
    },
    workflowRuns: {
      base: () => apiPathDefinitions.workflows.workflowRuns.base,
      byId: (runId: string) => apiPathDefinitions.workflows.workflowRuns.byId(runId),
      byRunNumber: (runNumber: number | string) =>
        apiPathDefinitions.workflows.workflowRuns.byRunNumber(runNumber),
      updates: () => apiPathDefinitions.workflows.workflowRuns.updates,
      logs: (runId: string) => apiPathDefinitions.workflows.workflowRuns.logs(runId),
      logsByRunNumber: (runNumber: number | string) =>
        apiPathDefinitions.workflows.workflowRuns.logsByRunNumber(runNumber),
      logUpdates: (runId: string) => apiPathDefinitions.workflows.workflowRuns.logUpdates(runId),
      cancel: (runId: string) => apiPathDefinitions.workflows.workflowRuns.cancel(runId),
      retry: (runId: string) => apiPathDefinitions.workflows.workflowRuns.retry(runId),
      stats: () => apiPathDefinitions.workflows.workflowRuns.stats,
      outputFilesDownload: (runId: string) =>
        apiPathDefinitions.workflows.workflowRuns.outputFilesDownload(runId),
      report: (runId: string) => apiPathDefinitions.workflows.workflowRuns.report(runId),
      reportByRunNumber: (runNumber: number | string) =>
        apiPathDefinitions.workflows.workflowRuns.reportByRunNumber(runNumber),
    },
  },
  products: {
    base: () => apiPathDefinitions.products.base,
    groups: () => apiPathDefinitions.products.groups,
    lots: (productId: string) => apiPathDefinitions.products.lots(productId),
    lotsMetrics: (productId: string) => apiPathDefinitions.products.lotsMetrics(productId),
  },
  reports: {
    download: (reportId: string, type?: 'PDF' | 'DOCX' | 'PPT') =>
      apiPathDefinitions.reports.download(reportId, type),
    attachmentDownload: (reportId: string, attachmentId: string) =>
      apiPathDefinitions.reports.attachmentDownload(reportId, attachmentId),
    comments: (reportId: string, params?: { limit?: number; offset?: number }) =>
      apiPathDefinitions.reports.comments(reportId, params),
    exports: (params?: {
      report_id?: string | null
      active_only?: boolean
      limit?: number
      offset?: number
    }) => apiPathDefinitions.reports.exports(params),
    exportsStream: () => apiPathDefinitions.reports.exportsStream,
  },
  dataSources: {
    base: () => apiPathDefinitions.dataSources.base,
  },
  dataSourceConnections: {
    base: () => apiPathDefinitions.dataSourceConnections.base,
    byId: (connectionId: string) => apiPathDefinitions.dataSourceConnections.byId(connectionId),
    test: (connectionId: string) => apiPathDefinitions.dataSourceConnections.test(connectionId),
  },
  improvements: {
    base: () => apiPathDefinitions.improvements.base,
  },
  productConfigurations: {
    base: () => apiPathDefinitions.productConfigurations.base,
  },
  productConfiguration: {
    byId: (id: string) => apiPathDefinitions.productConfiguration.byId(id),
  },
  userProducts: {
    base: () => apiPathDefinitions.userProducts.base,
  },
  policies: {
    base: () => apiPathDefinitions.policies.base,
    byProduct: (productId: string) =>
      `${apiPathDefinitions.policies.base}?product_id=${encodeURIComponent(productId)}`,
  },
  excursions: {
    base: () => apiPathDefinitions.excursions.base,
    metrics: () => apiPathDefinitions.excursions.metrics,
    byId: (eventId: string) => apiPathDefinitions.excursions.byId(eventId),
    ticket: (eventId: string) => apiPathDefinitions.excursions.ticket(eventId),
  },
  feedback: {
    apply: (commentId: string) => apiPathDefinitions.feedback.apply(commentId),
    delete: (commentId: string) => apiPathDefinitions.feedback.delete(commentId),
  },
} as const

export { getApiBaseUrl, apiPaths, apiPathKeys }
